/* eslint-disable */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { UserContext } from '../../../App';
import { Link } from 'react-router-dom';
import styles from './accounting.module.css';
import story from '../../form/accounting/story.module.css';
import dataLayerPush from '../../../utils/data-layer';
import { isImmoNaf, premiumPriceFor, tierPrices } from '../../../utils/gestion-pricing';

function GestionAccounting() {
    const { company, handleChange } = useContext(UserContext);

    const companyName = company.form.pappers?.denomination || company.form.dealname || 'votre entreprise';
    const codeNaf     = company.form.pappers?.code_naf || '';
    const isImmo      = isImmoNaf(codeNaf);

    // --- État BIC (CA) -------------------------------------------------------
    const initialCa = parseInt(company.form.chiffre_affaires) || 0;
    const [caInput,  setCaInput]  = useState(initialCa > 0 ? initialCa.toLocaleString('fr-FR') : '');
    const [caSlider, setCaSlider] = useState(initialCa);

    // --- État IMMO (nb_biens) ------------------------------------------------
    const [nbBiens, setNbBiens] = useState(parseInt(company.form.nb_biens) || 1);

    // Flag pour éviter que le useEffect au mount écrase la valeur existante avec 0
    const [hasInteracted, setHasInteracted] = useState(initialCa > 0);

    // --- Prix (Premium tirée de la grille, Débutant affiché sur cette page) ---
    const premium = useMemo(() => {
        return premiumPriceFor({ ca: caSlider, codeNaf, nbBiens });
    }, [caSlider, codeNaf, nbBiens]);

    const { debutant: minPrice } = useMemo(() => tierPrices(premium), [premium]);

    // Persistance dans le contexte global — seulement après une interaction utilisateur
    useEffect(() => {
        if (!isImmo && hasInteracted) {
            handleChange({ name: 'form.chiffre_affaires', value: String(caSlider) });
        }
    }, [caSlider, isImmo, hasInteracted]);

    useEffect(() => {
        if (isImmo && hasInteracted) {
            handleChange({ name: 'form.nb_biens', value: String(nbBiens) });
        }
    }, [nbBiens, isImmo, hasInteracted]);

    // --- Handlers BIC --------------------------------------------------------
    function handleSliderChange(e) {
        const value = parseInt(e.target.value);
        setHasInteracted(true);
        setCaSlider(value);
        setCaInput(value.toLocaleString('fr-FR'));
    }

    function handleCaInputChange(e) {
        const raw   = e.target.value.replace(/[^0-9]/g, '');
        const value = parseInt(raw) || 0;
        setHasInteracted(true);
        setCaInput(raw ? value.toLocaleString('fr-FR') : '');
        setCaSlider(Math.min(value, 1_500_000));
    }

    // --- Handlers IMMO -------------------------------------------------------
    function handleNbBiensChange(e) {
        const value = Math.max(1, parseInt(e.target.value) || 1);
        setHasInteracted(true);
        setNbBiens(value);
    }

    function incrementNbBiens() { setHasInteracted(true); setNbBiens(n => n + 1); }
    function decrementNbBiens() { setHasInteracted(true); setNbBiens(n => Math.max(1, n - 1)); }

    const handleClickForDataLayer = () => {
        // Persister la valeur au moment du clic même si l'utilisateur n'a pas touché le slider
        if (!isImmo) {
            handleChange({ name: 'form.chiffre_affaires', value: String(caSlider) });
        } else {
            handleChange({ name: 'form.nb_biens', value: String(nbBiens) });
        }
        dataLayerPush({ event: 'Clickbutton', eventLabel: 'Gestion - Accounting' });
    };

    return (
        <div className="self-step main-19">
            <div className={`global ${styles.global}`}>
                <div className="questions">Votre formule de comptabilité</div>
                <div className={styles.details} style={{ marginBottom: '10px' }}>
                    🌟 Comptastar gère la comptabilité de {companyName} de A à Z.
                </div>
                <div className={`sub-details ${styles['sub-details']}`}>
                    <div className={styles.arrow}>Bilan, liasse fiscale, compte de résultat, annexes, déclaration de TVA et télédéclaration au centre des impôts</div>
                    <div className={styles.arrow}>Comptable dédié avec des conseils en juridique, social et fiscalité</div>
                    <div className={styles.arrow}>Logiciel comptable intelligent en illimité (suivi de votre trésorerie et de la comptabilité en instantané)</div>
                </div>

                {/* ---- Saisie BIC (CA) ---- */}
                {!isImmo && (
                    <div className={styles.caSection}>
                        <div className={styles.caTitle}>
                            Veuillez rentrer votre chiffre d&apos;affaires de l&apos;année précédente pour estimer les honoraires
                        </div>
                        <div className={styles.caControls}>
                            <div className={styles.caInputWrapper}>
                                <input
                                    type="text"
                                    className={styles.caInput}
                                    placeholder="Ex : 150 000"
                                    value={caInput}
                                    onChange={handleCaInputChange}
                                />
                                <span className={styles.caUnit}>€ / an</span>
                            </div>
                            <input
                                type="range"
                                className={styles.caSlider}
                                min="0"
                                max="1500000"
                                step="10000"
                                value={caSlider}
                                onChange={handleSliderChange}
                            />
                            <div className={styles.caLabels}>
                                <span>0 €</span>
                                <span>500 000 €</span>
                                <span>1 000 000 €</span>
                                <span>1 500 000 €</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ---- Saisie IMMO (nb_biens) ---- */}
                {isImmo && (
                    <div className={styles.caSection}>
                        <div className={styles.caTitle}>
                            Combien de biens immobiliers gérez-vous ?
                        </div>
                        <div className={styles.caControls}>
                            <div className={styles.nbBiensWrapper}>
                                <button
                                    type="button"
                                    className={styles.nbBiensBtn}
                                    onClick={decrementNbBiens}
                                    aria-label="Diminuer"
                                >−</button>
                                <input
                                    type="number"
                                    min="1"
                                    className={styles.nbBiensInput}
                                    value={nbBiens}
                                    onChange={handleNbBiensChange}
                                />
                                <button
                                    type="button"
                                    className={styles.nbBiensBtn}
                                    onClick={incrementNbBiens}
                                    aria-label="Augmenter"
                                >+</button>
                                <span className={styles.caUnit}>bien{nbBiens > 1 ? 's' : ''}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.pricePreview}>
                    <span className={styles.priceLabel}>Tarif à partir de</span>
                    <span className={styles.priceAmount}>{minPrice}€</span>
                    <span className={styles.priceUnit}>/ mois HT</span>
                </div>

                <div className="answers">
                    <Link onClick={handleClickForDataLayer} to='/gestion/recap'>
                        <div className={`cta ${styles.button}`}>Voir les formules disponibles</div>
                    </Link>
                </div>

                {/* ── Pourquoi Comptastar ── */}
                <div className={story['our-story']}>
                    <div className="questions">Pourquoi Comptastar est le comptable qu&apos;il me faut ?</div>
                    <div className={story['didier-presentation']}>
                        <img alt="Président de Comptastar Didier" src="/didier.png" className={story.fst} />
                        <img alt="Président de Comptastar Didier plus rapproché" src="/didier-2.png" className={story.scd} />
                    </div>

                    <div className={story['main-value']}>
                        <div className={story.left}>
                            <div className={story.value}>
                                <div className={story.icon}>✅</div>
                                <h2>Expertise</h2>
                                <div className={story.description}>Membre de l&apos;<span className={story.bold}>Ordre des Experts-Comptables</span> et spécialisé dans la comptabilité et la création de sociétés, Comptastar vous accompagne sur <span className={story.bold}>tous les projets de votre entreprise</span> (missions sociales, juridiques et fiscales). Le plus ? Votre Comptable dédié sera un expert de votre secteur d&apos;activité.</div>
                            </div>
                            <div className={story.value}>
                                <div className={story.icon}>💰</div>
                                <h2>Tarifs justes</h2>
                                <div className={story.description}>Clé en main et sans engagement, optimisez facilement vos coûts comptables et optimisez fiscalement votre activité. <span className={story.bold}>Réalisez des économies</span> sans dégrader le service comptable.</div>
                            </div>
                        </div>
                        <div className={story.right}>
                            <div className={story.value}>
                                <div className={story.icon}>📱</div>
                                <h2>Simplicité</h2>
                                <div className={story.description}>Profitez de la <span className={story.bold}>meilleure application</span> (sur Mobile et Ordinateur) de comptabilité du marché et <span className={story.bold}>restez connecté à votre comptable dédié</span>. En un clin d&apos;oeil, consultez vos factures, flux de trésorerie et les documents comptables et fiscaux.</div>
                            </div>
                            <div className={story.value}>
                                <div className={story.icon}>🤝</div>
                                <h2>Accompagnement</h2>
                                <div className={story.description}>Avec <span className={story.bold}>plusieurs milliers de Français accompagnés</span> par le cabinet, Comptastar est présent partout en France* et vous épaule à toutes les étapes de votre activité : consulting, juridique, conseils comptables &amp; fiscaux.<br /><br /><span>📍 *Bordeaux, Paris et Perpignan</span></div>
                            </div>
                        </div>
                        <div className={`${story.value} ${story.satisfaction}`}>
                            <div className={story.icon}>😍</div>
                            <h2>Satisfaction</h2>
                            <div className={story.description}>Comptastar rend heureux ses clients et c&apos;est prouvé !<br /> Plus de <span className={story.bold}>100 000 Français satisfaits</span>.</div>
                        </div>
                    </div>
                </div>

                <div className="actions">
                    <Link to='/gestion/contact-informations'><div className="prev"><div className="arrow"></div>Retour</div></Link>
                    <div className="next" style={{ display: 'none' }}>Continuer</div>
                </div>
            </div>
        </div>
    );
}

export default GestionAccounting;
